"use client";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { elevation } from "../tokens/elevation";
import { motion } from "../tokens/motion";
import { radius } from "../tokens/radius";

import type { ReactNode } from "react";

/**
 * Deux lignes réservées au titre, toujours.
 *
 * Sans hauteur fixe, une grille de cards dont les titres font une ou deux
 * lignes désaligne ville, prix et bouton d'une colonne à l'autre. Seules les
 * cards Produit le faisaient ; les cards Itinéraire, non — d'où des hauteurs
 * de grille incohérentes entre deux listings du même écran.
 */
const TITLE_LINES = 2;
const TITLE_MIN_HEIGHT = "2.5em";

/** Opacité d'un item incomplet : lisible, mais visiblement en retrait. */
const DIMMED_OPACITY = 0.55;

/**
 * Ce qu'on met dans la ligne de contexte quand l'item n'a rien à y dire.
 *
 * Insécable et non une espace ordinaire : une espace ordinaire se réduirait à
 * néant et la ligne ne réserverait plus sa hauteur.
 */
const NON_BREAKING_SPACE = "\u00A0";

export type CatalogCardProps = {
  /** Le carousel ou la couverture. Occupe le haut de la card, coins arrondis. */
  media: ReactNode;
  /** Marqueur au-dessus du titre — « données incomplètes ». */
  badge?: ReactNode;
  /** Chips de région, entre le badge et le titre. */
  chips?: ReactNode;
  /** Toujours sur deux lignes réservées, coupé au-delà. */
  title: string;
  /**
   * Une ligne de contexte sous le titre : ville, durée.
   *
   * La ligne est **toujours** rendue, vide si l'item n'a rien à y mettre :
   * c'est ce qui garde les prix d'une même rangée alignés.
   */
  meta?: string | null;
  price?: ReactNode;
  /**
   * Pied de card, hors du contenu estompé.
   *
   * Un item incomplet est grisé, mais son bouton « Modifier dans Odys » doit
   * rester pleinement lisible — c'est précisément l'action qu'on demande
   * (#2146).
   */
  action?: ReactNode;
  /**
   * Rend la card entière cliquable, vers Odys. Toujours une URL externe : les
   * deux appelants pointent sur `editInOdysUrl`.
   */
  href?: string | null;
  /**
   * Appelé au clic sur la card quand elle est un lien. Le carousel d'images
   * stoppe la propagation de ses flèches : naviguer entre les photos ne le
   * déclenche pas.
   */
  onClick?: () => void;
  /** Item incomplet : contenu estompé, `action` exclue. */
  dimmed?: boolean;
  "data-testid"?: string;
};

/**
 * Card d'un item de catalogue, en vue grille.
 *
 * Elle porte le châssis — surface, rayons, élévation au survol, estompage d'un
 * item incomplet, lien externe optionnel — et la **typographie** du titre et de
 * la ligne de contexte. Le métier reste chez l'appelant, dans les slots.
 *
 * Le titre et la ligne de contexte sont dans le châssis et non dans un slot
 * libre parce que c'est là qu'était la divergence : ce sont eux qui décident
 * qu'une rangée de cards s'aligne.
 *
 * Le survol emprunte `motion.lift` à `SurfaceCard` : les deux cards se
 * soulèvent, elles doivent le faire du même mouvement.
 */
export function CatalogCard({
  media,
  badge,
  chips,
  title,
  meta,
  price,
  action,
  href,
  onClick,
  dimmed = false,
  "data-testid": dataTestId,
}: CatalogCardProps) {
  const isLink = href !== undefined && href !== null;
  const linkProps = isLink
    ? ({
        component: "a",
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick,
      } as const)
    : {};

  const dimmedSx = {
    opacity: dimmed ? DIMMED_OPACITY : 1,
    transition: "opacity .15s ease",
  } as const;

  return (
    <Box
      {...linkProps}
      data-testid={dataTestId}
      data-incomplete={dimmed ? "true" : undefined}
      sx={{
        p: 0.5,
        borderRadius: radius.lg,
        bgcolor: "background.paper",
        // Une card qui se soulève part de rien : empiler `card` puis
        // `cardHover` donnerait une surface déjà soulevée au repos, et le
        // survol ne dirait plus grand-chose. Une card non cliquable ne se
        // soulève pas — elle garde l'ombre de repos.
        boxShadow: isLink ? "none" : elevation.card,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        textDecoration: "none",
        color: "inherit",
        cursor: isLink ? "pointer" : "default",
        transition: `box-shadow ${motion.lift}`,
        ...(isLink ? { "&:hover": { boxShadow: elevation.cardHover } } : {}),
      }}
    >
      <Box sx={{ borderRadius: radius.md, overflow: "hidden", ...dimmedSx }}>
        {media}
      </Box>
      <Box sx={{ p: 1.5, ...dimmedSx }}>
        {badge !== undefined && <Box sx={{ mb: 1 }}>{badge}</Box>}
        {chips !== undefined && <Box sx={{ mb: 0.75 }}>{chips}</Box>}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: TITLE_LINES,
            overflow: "hidden",
            minHeight: TITLE_MIN_HEIGHT,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          noWrap
          sx={{ color: "text.secondary", mt: 0.5 }}
        >
          {meta === undefined || meta === null ? NON_BREAKING_SPACE : meta}
        </Typography>
        {price !== undefined && <Box sx={{ mt: 0.75 }}>{price}</Box>}
      </Box>
      {action !== undefined && <Box sx={{ px: 1.5, pb: 1.5 }}>{action}</Box>}
    </Box>
  );
}
